import React from 'react';

import {
  BodyText,
  BulletList,
  CardShell,
  CardVariantContext,
  Heading,
  hitArea,
  MethodButton,
  NotNowButton,
  PrimaryButton
} from 'components/RegistrationPrototype/d4Primitives';
import { D4_TOKENS, D4Skin, typeStyle } from 'components/RegistrationPrototype/d4Tokens';

import {
  BiometryKind,
  biometryName,
  biometryTitle,
  SensorDisc,
  SensorGlyph
} from './biometricGlyphs';

/**
 * Biometric login surfaces (EN-5415) — the design layer for Harvey's flow,
 * drawn entirely from the RegistrationPrototype d4 system so biometric login
 * and the registration/passkey work read as one product family.
 *
 * Mapping to the shipped/PR components these replace visually:
 *   EnrollmentOfferCard  → game/src/components/Login/BiometricEnrollmentPrompt.tsx
 *                          + apps/auth PasskeyEnrollmentModal.jsx (AC2)
 *   UnlockRetryCard      → the state QA flagged missing (Noe 2026-08-17):
 *                          biometric no-match currently redirects to Login and
 *                          auto-disables the setting. This card is the fix's
 *                          shape: retry + password escape, enrollment INTACT.
 *   BiometricsChangedCard→ AC9 (credentials invalidated / biometrics changed)
 *   LockedOutCard        → OS lockout after repeated failures (AC6 edge)
 *   EnrolledConfirmation → post-enroll success beat
 *   BiometricMethodButton→ the login screen's entry point (AC4)
 *   SettingsBiometricRow → account settings toggle (AC8), disabled state (AC10)
 *
 * ALL COPY DRAFT pending Legal/Compliance. Synthetic data only. No production
 * source edits — additive prototype files only.
 */

/* ── Shared bits ─────────────────────────────────────────────────────────── */

/**
 * Interaction states as real CSS (prototype polish standard): press scale on
 * the primary/method buttons comes from the d4 primitives themselves; this
 * block only adds what the new surfaces introduce. Reduced motion collapses
 * the sheet slide to a fade (apple-design §14).
 */
export const BIOMETRIC_KEYFRAMES = `
@keyframes bioSheetIn {
  from { transform: translateY(24px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
@keyframes bioFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .bio-anim { animation: bioFadeIn 200ms ease both !important; }
}
`;

const stackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 14,
  textAlign: 'center',
  padding: '6px 6px 14px'
};

/**
 * Content height reserved by the cards that REPLACE the offer inside an open
 * drawer — the confirmation and the soft landing (owner 2026-08-20).
 *
 * The enrolment flow is one drawer showing three things in turn: the offer, the
 * OS prompt, then the outcome. Sized to its own content, the outcome card was
 * ~170px shorter than the offer, so the drawer collapsed while the player was
 * looking at it: the title jumped down the screen and the whole thing crowded
 * into the bottom strip of the display. Holding one height means the surface
 * stays put and only its contents change — and because every card in the family
 * opens with the same 54px disc under the same 52px of shell padding, the
 * titles land on the same line, which is what makes the swap read as one
 * continuous sheet.
 *
 * 364 is the offer card's measured content height at 390pt (the tallest in the
 * flow). It is a FLOOR, not a fixed height — a card that needs more, because
 * text wrapped or the player scaled their type up, simply grows past it and
 * nothing clips. Re-measure by reading the CardBody stack's height in the
 * PresentationByPlatform story if the offer's content changes.
 *
 * Deliberately NOT applied to the cards that appear on their own — the settings
 * "unavailable" sheet, the unlock states. Nothing precedes those, so there is no
 * height for them to hold, and a floor would only pad them with dead space.
 */
const SHEET_FLOW_CONTENT_MIN = 364;

/**
 * CardShell owns the sheet presentation itself: in the 'sheet' variant it
 * draws its own grabber and deepens the body padding (see d4Primitives) — so
 * these cards add NOTHING for sheets except suppressing the close circle.
 * The ✕ renders unconditionally unless `hideChrome`, and on a sheet it is a
 * third dismiss affordance on top of the grabber and "Not now" — chrome noise
 * (apple-design: every element earns its place). Modal keeps it: on desktop
 * web the ✕ IS the expected dismiss.
 *
 * `holdFlowHeight` opts a card into the reserved height above. Sheets only:
 * on desktop each dialog is its own centred object, free to be its own size.
 */
function CardBody({
  children,
  holdFlowHeight
}: {
  children: React.ReactNode;
  holdFlowHeight?: boolean;
}) {
  const sheet = useIsSheet();
  return (
    <div
      style={
        holdFlowHeight && sheet ? { ...stackStyle, minHeight: SHEET_FLOW_CONTENT_MIN } : stackStyle
      }>
      {children}
    </div>
  );
}

/**
 * Sends the trailing group to the foot of a reserved-height sheet, so the last
 * element sits where the offer's last element sat. Without it the spare height
 * pools under the final line and the card looks unfinished rather than roomy.
 */
function footOfSheet(sheet: boolean): React.CSSProperties | null {
  return sheet ? { marginTop: 'auto' } : null;
}

/**
 * Centres a card's message block (icon + title + body) in the spare height of
 * a reserved-height sheet, between the grabber above and the actions below
 * (owner 2026-08-20). With the block top-anchored, all the reserved height
 * pooled in one dead band mid-card; centring splits it evenly around the
 * message, which is where the eye lands anyway. Inherits the stack's own
 * alignment and gap so the block is indistinguishable from unwrapped children
 * on modal, where there is no spare height to distribute.
 */
function sheetCenterZone(sheet: boolean): React.CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
    alignSelf: 'stretch',
    ...(sheet ? { flex: 1, justifyContent: 'center' } : null)
  };
}

/** True in the sheet variant — used to hide the modal chrome (close circle). */
function useIsSheet(): boolean {
  return React.useContext(CardVariantContext) === 'sheet';
}

/** Full-width column for the button pair, gap matching the registration cards. */
const buttonColStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  width: '100%',
  marginTop: 4
};

/* ── 0. Presentation ─────────────────────────────────────────────────────── */

// The drawer/dialog host moved to BiometricSheet.tsx when the sheet gained real
// drag-to-dismiss physics — the gesture is enough code to deserve its own file,
// and its maths is unit-tested in sheetDrag.ts.
export {
  BIOMETRIC_COMPACT_MAX_PX,
  BiometricSurfaceHost,
  resolveBiometricPresentation,
  useSheetDismiss,
  useSheetEngaged
} from './BiometricSheet';

// Imported (not just re-exported) because every card below routes its own
// dismiss through it: tapping "Not now" must run the same slide-down + blur
// fade as dragging the sheet away, or the two exits feel like different
// products (Jason, on-device 2026-08-19).
import { useSheetDismiss as useDismiss } from './BiometricSheet';

/* ── 1. Enrollment offer (AC2) ───────────────────────────────────────────── */

export function EnrollmentOfferCard({
  skin,
  biometry,
  busy,
  onEnroll,
  onSkip
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  busy?: boolean;
  onEnroll?: () => void;
  onSkip?: () => void;
}) {
  const t = D4_TOKENS[skin];
  const name = biometryName(biometry);
  const sheet = useIsSheet();
  const dismiss = useDismiss();
  const skip = busy ? undefined : () => dismiss(onSkip);
  return (
    <CardShell skin={skin} hideChrome={sheet} onClose={skip}>
      <CardBody>
        <SensorDisc kind={biometry} disc={t.disc} />
        <Heading skin={skin} center>
          Sign in with {name}
        </Heading>
        <BodyText skin={skin} center>
          Use {name} to sign in on this device.
        </BodyText>
        <BulletList
          skin={skin}
          items={[
            { icon: 'key', text: 'Sign in without typing a password or code' },
            {
              icon: 'lock',
              text:
                biometry === 'faceid'
                  ? 'Your face data stays on this device'
                  : 'Your fingerprint stays on this device'
            },
            { icon: 'devices', text: 'Turn it off in Settings whenever you want' }
          ]}
        />
        <div style={buttonColStyle}>
          <PrimaryButton skin={skin} disabled={busy} onClick={onEnroll}>
            {busy ? 'Setting up…' : `Enable ${biometryTitle(biometry)}`}
          </PrimaryButton>
          <NotNowButton skin={skin} onClick={skip} />
        </div>
      </CardBody>
    </CardShell>
  );
}

/* ── 2. No-match → retry (the QA fix, Noe 2026-08-17) ────────────────────── */

/**
 * The state that must exist for QA to go green: a failed match keeps the
 * player exactly where they were, keeps enrollment ON, and offers the two
 * honest paths — try again, or use the password. Copy is calm and blame-free
 * (better-writing: errors say how to fix; zero playfulness at auth stakes).
 */
export function UnlockRetryCard({
  skin,
  biometry,
  busy,
  attempt,
  maxAttempts,
  onRetry,
  onUsePassword,
  onClose
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  /** True while the retry's OS prompt is up — the scan runs from this card. */
  busy?: boolean;
  /**
   * How many scans this in-app session has tried so far, out of maxAttempts —
   * OUR OWN count, not the OS's. The plugin never tells us how many scans the
   * device will allow before it locks out on its own (that's the separate
   * 'locked-out' state), so this is purely informational: proof the retry is
   * really happening, the same reasoning as the exchange's "Reconnecting…
   * (2/3)" toast. Omit either prop to render without it.
   */
  attempt?: number;
  maxAttempts?: number;
  onRetry?: () => void;
  onUsePassword?: () => void;
  onClose?: () => void;
}) {
  const t = D4_TOKENS[skin];
  // Title-cased: these strings LEAD with the sensor name, and "fingerprint"
  // must not open a heading lowercase. Face ID / Touch ID are unchanged.
  const name = biometryTitle(biometry);
  const sheet = useIsSheet();
  const dismiss = useDismiss();
  const close = busy ? undefined : () => dismiss(onClose);
  // QA (Harvey, 2026-08-26): the fallback used to sit right next to Retry from
  // the first failure, which reads as "give up any time you like" rather than
  // "keep trying". It's now held back until the attempt count actually runs
  // out — retry is the ONLY option while attempts remain, so the player
  // exhausts them for real before another method appears. With no attempt
  // count passed in at all, default to always showing it (unchanged for any
  // caller that doesn't track attempts).
  const attemptsExhausted =
    typeof attempt !== 'number' || typeof maxAttempts !== 'number' || attempt >= maxAttempts;
  return (
    <CardShell skin={skin} hideChrome={sheet} onClose={close}>
      <CardBody>
        <SensorDisc kind={biometry} disc={t.disc} />
        {/* NOT "unable to recognize" / "didn't match": iOS reports the exact
            same cancel code for "failed twice, pressed the only button
            offered" as for "backed out without even trying" — no-match and
            cancel are indistinguishable to us, so the heading must be true
            for both. "Didn't complete" makes no claim about WHY, only that
            it didn't happen (same rule as the enrollment soft landing). */}
        <Heading skin={skin} center>
          {biometryTitle(biometry)} sign-in didn't complete
        </Heading>
        <BodyText skin={skin} center>
          {attemptsExhausted
            // QA (Final QA copy): body text must never say "try again" in a
            // state where the Retry button is absent — Retry is REPLACED by
            // the fallback below once attempts run out, not joined by it, so
            // the copy here can only point at the option actually on screen.
            ? `${name} sign-in is still on for this account. Use another sign-in method for now.`
            : `${biometryTitle(biometry)} is still set up for this account.`}
        </BodyText>
        <div style={buttonColStyle}>
          {/* Once attempts are used up there is nothing left to retry — Retry
              is REPLACED by the fallback, not joined by it (QA — Harvey,
              2026-08-26: showing both after the count runs out still let the
              player keep hammering the sensor instead of switching methods). */}
          {!attemptsExhausted && (
            <PrimaryButton skin={skin} disabled={busy} onClick={onRetry}>
              {busy ? 'Waiting for ' + biometryTitle(biometry) + '…' : 'Try again'}
            </PrimaryButton>
          )}
          {/* "another method", not "password": the login page also offers
              Google/Apple sign-in, so naming just the password would be wrong
              for anyone who originally signed up through a social provider. */}
          {attemptsExhausted && (
            <MethodButton
              skin={skin}
              onClick={busy ? undefined : () => dismiss(onUsePassword)}
              icon={<SensorGlyphKeyFallback color={t.methodBtn.font.color} />}>
              Use another login method
            </MethodButton>
          )}
        </div>
        <span style={{ ...typeStyle(t.micro) }}>Manage {name} in Settings</span>
      </CardBody>
    </CardShell>
  );
}

/** Small key glyph for the password fallback method button. */
function SensorGlyphKeyFallback({ color }: { color: string }) {
  return (
    <svg
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
      style={{ flex: 'none' }}>
      <circle cx='8.5' cy='12' r='3.5' />
      <path d='M12 12h8' />
      <path d='M17.5 12v2.6' />
      <path d='M20 12v2.6' />
    </svg>
  );
}

/* ── 2b. Exchange couldn't reach the server after every retry ───────────── */

/**
 * Shown when exchangeRefreshToken exhausts every retry without a response
 * (nativeLogin's 'network' reason) — the scan itself succeeded, but the app
 * could never confirm the sign-in with the server. Distinct from
 * UnlockRetryCard (a no-match/cancelled scan) and from BiometricsChangedCard
 * (AC9, a credential Descope actively rejected): here the credential was
 * never actually checked, so enrollment stays on and biometric sign-in is
 * still there to try next time — this only offers another way in for now.
 */
export function ConnectionTroubleCard({
  skin,
  biometry,
  busy,
  onRetry,
  onUsePassword,
  onClose
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  /** True while the retry's OS prompt is up — the scan runs from this card. */
  busy?: boolean;
  onRetry?: () => void;
  onUsePassword?: () => void;
  onClose?: () => void;
}) {
  const t = D4_TOKENS[skin];
  const sheet = useIsSheet();
  const dismiss = useDismiss();
  const close = busy ? undefined : () => dismiss(onClose);
  return (
    <CardShell skin={skin} hideChrome={sheet} onClose={close}>
      <CardBody>
        <SensorDisc kind={biometry} disc={t.disc} />
        <Heading skin={skin} center>
          Want to use another login method?
        </Heading>
        <BodyText skin={skin} center>
          We couldn’t complete sign-in right now. {biometryTitle(biometry)} sign-in is still on for
          this account — try again, or use another sign-in method.
        </BodyText>
        <div style={buttonColStyle}>
          <PrimaryButton skin={skin} disabled={busy} onClick={onRetry}>
            {busy ? 'Trying again…' : 'Try again'}
          </PrimaryButton>
          {/* "another method", not "password" — the login page also offers
              Google/Apple sign-in (see UnlockRetryCard's identical note). */}
          <MethodButton
            skin={skin}
            onClick={busy ? undefined : () => dismiss(onUsePassword)}
            icon={<SensorGlyphKeyFallback color={t.methodBtn.font.color} />}>
            Use another login method
          </MethodButton>
        </div>
      </CardBody>
    </CardShell>
  );
}

/* ── 3. Biometrics changed on device (AC9) ───────────────────────────────── */

export function BiometricsChangedCard({
  skin,
  biometry,
  onUsePassword,
  onDismiss
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  onUsePassword?: () => void;
  onDismiss?: () => void;
}) {
  const t = D4_TOKENS[skin];
  const name = biometryName(biometry);
  const sheet = useIsSheet();
  const dismiss = useDismiss();
  return (
    <CardShell skin={skin} hideChrome={sheet} onClose={() => dismiss(onDismiss)}>
      <CardBody>
        <SensorDisc kind={biometry} disc={t.disc} />
        {/* Copy names the EFFECT, not a cause. This card fires whenever the
            saved sign-in stops working — biometrics changed on the device
            (AC9), the token expired after a long absence, or the keystore
            entry became unreadable — and we cannot tell those apart from
            here. "changed on this device" was a guess that would be wrong two
            times out of three; "was turned off" is what we actually know. */}
        <Heading skin={skin} center>
          {biometryTitle(biometry)} sign-in was turned off
        </Heading>
        <BodyText skin={skin} center>
          The saved sign-in for this device stopped working, so {name} is off here. Sign in
          again to turn it back on.
        </BodyText>
        <div style={buttonColStyle}>
          <PrimaryButton skin={skin} onClick={() => dismiss(onUsePassword)}>
            Sign in another way
          </PrimaryButton>
          <NotNowButton skin={skin} onClick={() => dismiss(onDismiss)} />
        </div>
      </CardBody>
    </CardShell>
  );
}

/* ── 4. OS lockout (AC6 edge) ────────────────────────────────────────────── */

/**
 * AC12's card, plus a retry (QA — Harvey, 2026-08-26): every other failure
 * card in this family offers a way to try biometrics again in place, and this
 * one hadn't — the OS lockout is often per-attempt or clears after a short
 * window, so "try again" can genuinely succeed a moment later, not just on a
 * device passcode unlock. Retry stays available EVERY time this card is
 * shown; if the device is still locked it just re-raises this same card,
 * which is a truthful outcome, not a broken one.
 *
 * COPY (QA — Harvey, 2026-08-26): "{biometry} is locked for now" led with the
 * OS's own, harsher framing — and because the OS lockout counter persists
 * across app launches, a tester can rack up prior failures across earlier
 * sessions and land here off what feels like a single wrong-finger try. The
 * heading now reads the same as every other failure in this family ("…sign-in
 * didn't complete" — same as UnlockRetryCard), so a lockout doesn't feel like
 * a categorically different, scarier event than a no-match.
 *
 * COPY (QA — Harvey, 2026-08-31): the passcode/screen-lock detail is real OS
 * behavior, not filler — but it only belongs once attempts are actually used
 * up. While Retry is still on offer the body just confirms nothing broke
 * ("still set up for this account"); the passcode tip replaces that sentence
 * entirely once the fallback takes over, rather than tacking onto it.
 */
export function LockedOutCard({
  skin,
  biometry,
  busy,
  attempt,
  maxAttempts,
  onRetry,
  onUsePassword,
  onClose
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  /** True while the retry's OS prompt is up — the scan runs from this card. */
  busy?: boolean;
  /** Same contract as UnlockRetryCard's — see there for the full rationale. */
  attempt?: number;
  maxAttempts?: number;
  onRetry?: () => void;
  onUsePassword?: () => void;
  onClose?: () => void;
}) {
  const t = D4_TOKENS[skin];
  const sheet = useIsSheet();
  const dismiss = useDismiss();
  const close = busy ? undefined : () => dismiss(onClose);
  // Same held-back-fallback rule as UnlockRetryCard (QA — Harvey, 2026-08-26):
  // retry is the only option while attempts remain.
  const attemptsExhausted =
    typeof attempt !== 'number' || typeof maxAttempts !== 'number' || attempt >= maxAttempts;
  return (
    <CardShell skin={skin} hideChrome={sheet} onClose={close}>
      <CardBody>
        <SensorDisc kind={biometry} disc={t.disc} />
        <Heading skin={skin} center>
          {biometryTitle(biometry)} sign-in didn't complete
        </Heading>
        {/* Two different jobs, so two different moments (QA — Harvey,
            2026-08-31): "still set up" reassures that nothing broke, which is
            only true and useful while Retry is still on offer. The passcode/
            screen-lock tip is real OS behavior (repeated failures pause the
            sensor until the device is unlocked with its passcode), but only
            once attempts actually run out — naming it on attempt 1 reads as
            the app already expecting failure. */}
        <BodyText skin={skin} center>
          {attemptsExhausted
            ? `Phones pause ${biometryName(biometry)} after too many tries. Unlocking your phone with your ${lockWord(biometry)} usually brings it back.`
            : `${biometryTitle(biometry)} is still set up for this account.`}
        </BodyText>
        <div style={buttonColStyle}>
          {/* Same replace-not-join rule as UnlockRetryCard (QA — Harvey,
              2026-08-26): once attempts are used up, Retry is gone — only the
              fallback remains. */}
          {!attemptsExhausted && (
            <PrimaryButton skin={skin} disabled={busy} onClick={onRetry}>
              {busy ? 'Waiting for ' + biometryTitle(biometry) + '…' : 'Try again'}
            </PrimaryButton>
          )}
          {attemptsExhausted && (
            <MethodButton
              skin={skin}
              onClick={busy ? undefined : () => dismiss(onUsePassword)}
              icon={<SensorGlyphKeyFallback color={t.methodBtn.font.color} />}>
              Use another login method
            </MethodButton>
          )}
        </div>
      </CardBody>
    </CardShell>
  );
}

/* ── 5. Enrolled confirmation ────────────────────────────────────────────── */

/**
 * Quiet success beat — tick tokens from the d4 system, no fireworks: turning
 * on a setting is a small win, not the registration finale (motion restraint;
 * the registration flow reserves Fireworks for account creation).
 *
 * ON THE "DONE" BUTTON (owner 2026-08-20). The first version had no control at
 * all: it auto-dismissed, so a button looked redundant. Holding the offer's
 * height exposed why that was wrong twice over. Composition first — the spare
 * height has to land somewhere, and a 10px caption alone at the foot of it
 * reads as an unfinished card, where a button reads as a card that ends. And
 * access second: with no focusable control, the only exits were a timer and a
 * drag, which is no exit at all for anyone using VoiceOver or a keyboard.
 *
 * So the bottom slot now mirrors the offer's — action, then caption — and the
 * two sheets rhyme structurally: disc, title, line, action.
 */
export function EnrolledConfirmation({
  skin,
  biometry,
  onDone
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  onDone?: () => void;
}) {
  const dismiss = useDismiss();
  const t = D4_TOKENS[skin];
  const name = biometryName(biometry);
  const sheet = useIsSheet();
  return (
    <CardShell skin={skin} hideChrome>
      <CardBody holdFlowHeight>
        {/* The message block rides centred in the sheet's reserved height —
            equal air above and below it, between grabber and Done
            (owner 2026-08-20). */}
        <div style={sheetCenterZone(sheet)}>
          <div
            aria-hidden='true'
            style={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              background: t.tick.bg,
              border: `1.5px solid ${t.tick.border}`,
              boxShadow: t.tick.glow,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <svg
              width='26'
              height='26'
              viewBox='0 0 24 24'
              fill='none'
              stroke={t.tick.stroke}
              strokeWidth={2}
              strokeLinecap='round'
              strokeLinejoin='round'>
              <path d='M5 12.5l4.2 4.3L19 7.5' />
            </svg>
          </div>
          <Heading skin={skin} center>
            {biometryTitle(biometry)} is on
          </Heading>
          <BodyText skin={skin} center>
            Next time you sign in, {name} is all you need.
          </BodyText>
        </div>
        <div style={buttonColStyle}>
          <PrimaryButton skin={skin} onClick={() => dismiss(onDone)}>
            Done
          </PrimaryButton>
        </div>
        <span style={{ ...typeStyle(t.micro) }}>Turn it off in Settings</span>
      </CardBody>
    </CardShell>
  );
}

/* ── 5b. Capable device, biometrics not set up ───────────────────────────── */

/**
 * THE GAP (found on-device 2026-08-19, Jason's iPhone 13):
 *
 * The plugin reported `biometryType: FACE_ID` — the hardware is right there —
 * but `strongBiometryIsAvailable: false` with `errorCode 3`
 * (BIOMETRICS_NOT_ENROLLED): no face registered on the device. Today that case
 * is invisible from end to end. useBiometricEnrollmentOffer calls clearOffer()
 * and returns without a word, and AccountMenuPanel renders its row behind
 * `biometricAvailableHere &&`, so the setting is not merely off — it is absent.
 * A player in this state cannot discover the feature from any surface in the
 * product, and never learns that a change on their own device would unlock it.
 *
 * That is a real slice of players: everyone who unlocks their phone with a
 * passcode. They are not incapable — they are one OS setting away.
 *
 * So this card sells the benefit FIRST (identical bullets to the enrollment
 * offer — same value, same words) and only then explains what is missing, with
 * the exact path. It never blocks: password login is untouched behind it.
 *
 * On the CTA: iOS has no public deep link to Face ID enrolment.
 * `UIApplication.openSettingsURLString` lands on this app's own Settings page,
 * one back-tap from Face ID & Passcode, and the `App-Prefs:` schemes that go
 * straight there are private API that App Review has rejected apps for. So the
 * button opens Settings and the written path carries the last step — honest
 * about what it can actually do rather than promising a jump it cannot make.
 */
export type BiometricBlockedReason = 'not-enrolled' | 'locked-out' | 'unsupported';

/**
 * "Passcode" is an iOS word. Android calls the same thing a screen lock, and
 * the fingerprint kind is the Android kind, so the lock word follows the
 * sensor (owner 2026-08-20 — every sensor-specific string in these cards must
 * resolve from `biometry`, never assume Face ID).
 */
const lockWord = (kind: BiometryKind): string =>
  kind === 'fingerprint' ? 'screen lock' : 'passcode';

/**
 * The written setup path and CTA, per sensor. iOS paths are exact and stable
 * enough to print. Android's varies by manufacturer (Pixel: Security ›
 * Fingerprint Unlock; Samsung: Security and privacy › Biometrics), so printing
 * one OEM's path would be wrong on the rest — the body carries a generic
 * instruction instead and no path chip renders.
 */
const SETUP_PATH: Record<BiometryKind, string | undefined> = {
  faceid: 'Settings › Face ID & Passcode › Set Up Face ID',
  touchid: 'Settings › Touch ID & Passcode › Add a Fingerprint',
  fingerprint: undefined
};

const BLOCKED_COPY: Record<
  BiometricBlockedReason,
  {
    /** `n` mid-sentence (lowercase fingerprint ok); `lead` opens the string. */
    heading: (n: string, lead: string) => string;
    body: (n: string, kind: BiometryKind) => string;
    path?: (kind: BiometryKind) => string | undefined;
    /** undefined = render no button — a CTA that can't act is worse than none. */
    cta?: (kind: BiometryKind) => string | undefined;
  }
> = {
  'not-enrolled': {
    heading: (n) => `Sign in with ${n}`,
    body: (n, kind) =>
      kind === 'fingerprint'
        ? `Set up fingerprint unlock in this device’s security settings once, and you can use it to sign in here.`
        : `Set up ${n} on this device once, and you can use it to sign in here.`,
    path: (kind) => SETUP_PATH[kind],
    // No button on Android: opening its security settings needs an intent we
    // have no plugin for, and a button that silently no-ops is a broken
    // promise. The body already carries the instruction.
    cta: (kind) => (kind === 'fingerprint' ? undefined : 'Open iOS Settings')
  },
  'locked-out': {
    heading: (_n, lead) => `${lead} is locked`,
    body: (n, kind) => `Unlock this device with its ${lockWord(kind)}, and ${n} works here again.`,
    // No CTA: onAction here only dismisses the explainer (see AccountMenuPanel/
    // ProfilePanel's onAction), it doesn't retry or re-check anything, so a
    // button labeled "Try again" would be a dead end. Omitting it makes the
    // NotNowButton render "Got it" instead, same as the unsupported variant.
  },
  unsupported: {
    heading: () => 'Biometric sign-in unavailable',
    // Shared by native (no strong sensor) and web (browser has no usable
    // platform authenticator), so it must not diagnose the hardware.
    body: () => 'Biometric sign-in can’t be used on this device or browser. Sign in another way.'
  }
};

export function BiometricUnavailableCard({
  skin,
  biometry,
  reason,
  onAction,
  onDismiss
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  reason: BiometricBlockedReason;
  onAction?: () => void;
  onDismiss?: () => void;
}) {
  const t = D4_TOKENS[skin];
  const name = biometryName(biometry);
  const copy = BLOCKED_COPY[reason];
  const ctaLabel = copy.cta?.(biometry);
  const sheet = useIsSheet();
  const dismiss = useDismiss();
  return (
    <CardShell skin={skin} hideChrome={sheet} onClose={() => dismiss(onDismiss)}>
      <CardBody>
        <SensorDisc kind={biometry} disc={t.disc} />
        <Heading skin={skin} center>
          {copy.heading(name, biometryTitle(biometry))}
        </Heading>
        {/* Benefit before obstacle: the player should know what they'd gain
            before being told what is missing, or the card reads as a chore. */}
        {reason !== 'unsupported' && (
          <BulletList
            skin={skin}
            items={[
              { icon: 'key', text: 'Sign in without typing a password or code' },
              { icon: 'lock', text: `Your ${name} data stays on this device` }
            ]}
          />
        )}
        <BodyText skin={skin} center>
          {copy.body(name, biometry)}
        </BodyText>
        {copy.path?.(biometry) && (
          <div
            style={{
              ...typeStyle(t.micro),
              textTransform: 'none',
              letterSpacing: 0,
              padding: '8px 12px',
              borderRadius: 8,
              border: `1px solid ${t.rule}`,
              width: '100%',
              boxSizing: 'border-box'
            }}>
            {copy.path(biometry)}
          </div>
        )}
        <div style={buttonColStyle}>
          {ctaLabel && (
            <PrimaryButton skin={skin} onClick={onAction}>
              {ctaLabel}
            </PrimaryButton>
          )}
          <NotNowButton skin={skin} onClick={() => dismiss(onDismiss)}>
            {ctaLabel ? 'Not now' : 'Got it'}
          </NotNowButton>
        </div>
      </CardBody>
    </CardShell>
  );
}

/* ── 5c. Enrolment didn't complete — the soft landing ────────────────────── */

/**
 * Shown when turning biometrics ON does not succeed (Jason, on-device
 * 2026-08-19).
 *
 * iOS gives the player two tries at the scan and then closes its own prompt.
 * Before this, ours closed too — the drawer simply vanished and the lobby was
 * back, with no word about what had happened or whether anything had been
 * enabled. From the player's side they asked for a thing, saw a scan fail, and
 * the product said nothing at all. Jarring, and it leaves them unsure whether
 * biometric sign-in is now on.
 *
 * So: state plainly that it is NOT on, that nothing else changed, and where to
 * go when they want it. No apology, no error styling — a failed face match is
 * an ordinary event, not a fault.
 *
 * The copy deliberately avoids naming a cause. iOS reports a cancel whether the
 * player backed out immediately or failed the scan and pressed the only button
 * their dialog offered, so we cannot tell those apart — and guessing wrong
 * ("the scan didn't finish" at someone who never scanned) is worse than
 * describing only what we know: it is not on, and nothing else changed.
 */
export function EnrollmentNotCompletedCard({
  skin,
  biometry,
  busy,
  onRetry,
  onDismiss
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  /** True while the retry's OS prompt is up — the scan runs from this card. */
  busy?: boolean;
  onRetry?: () => void;
  onDismiss?: () => void;
}) {
  const t = D4_TOKENS[skin];
  const name = biometryName(biometry);
  const sheet = useIsSheet();
  const dismiss = useDismiss();
  const close = busy ? undefined : () => dismiss(onDismiss);
  return (
    <CardShell skin={skin} hideChrome={sheet} onClose={close}>
      <CardBody holdFlowHeight>
        <SensorDisc kind={biometry} disc={t.disc} />
        <Heading skin={skin} center>
          {biometryTitle(biometry)} isn’t on yet
        </Heading>
        <BodyText skin={skin} center>
          Nothing changed — keep signing in the way you do now. You can turn {name} on any time.
        </BodyText>
        <div style={{ ...buttonColStyle, ...footOfSheet(sheet) }}>
          {/* Retry runs the scan from HERE — this card is the one place the
              player stays until it works or they leave (owner 2026-08-19). */}
          <PrimaryButton skin={skin} disabled={busy} onClick={onRetry}>
            {busy ? 'Setting up…' : `Try ${biometryTitle(biometry)} again`}
          </PrimaryButton>
          <NotNowButton skin={skin} onClick={close}>
            Not now
          </NotNowButton>
        </div>
        <span style={{ ...typeStyle(t.micro) }}>You can turn it on later in Settings</span>
      </CardBody>
    </CardShell>
  );
}

/* ── 6. Login-screen entry point (AC4) ───────────────────────────────────── */

/**
 * The returning player's way in — a d4 method button, sitting with the other
 * sign-in methods (Apple / Google / password) so biometric login reads as a
 * peer method, not a bolt-on. On web this is the auth app's button; on native
 * it is the button that fires unlockBiometric().
 */
export function BiometricMethodButton({
  skin,
  biometry,
  onClick
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  onClick?: () => void;
}) {
  const t = D4_TOKENS[skin];
  return (
    <MethodButton
      skin={skin}
      onClick={onClick}
      icon={
        <span style={{ color: t.methodBtn.font.color, display: 'flex' }}>
          <SensorGlyph kind={biometry} size={18} />
        </span>
      }>
      Sign in with {biometryTitle(biometry)}
    </MethodButton>
  );
}

/* ── 7. Settings toggle row (AC8, AC10) ──────────────────────────────────── */

/**
 * Account-settings row. Label names the ON state (better-writing §9) so the
 * switch never reads as a double negative. `unavailableReason` renders the
 * AC10 state — hardware absent or OS-level lockout — as a disabled row that
 * SAYS why instead of silently hiding (wayfinding: the player should learn
 * what their device can do, not wonder where the option went).
 */
export function SettingsBiometricRow({
  skin,
  biometry,
  enabled,
  unavailableReason,
  actionLabel,
  onAction,
  onToggle
}: {
  skin: D4Skin;
  biometry: BiometryKind;
  enabled: boolean;
  unavailableReason?: string;
  /** Present when the blocked state is something the player can act on. */
  actionLabel?: string;
  onAction?: () => void;
  onToggle?: (next: boolean) => void;
}) {
  const t = D4_TOKENS[skin];
  const name = biometryName(biometry);
  const disabled = !!unavailableReason;
  const trackOn = t.checkbox.onBg;
  const trackOff = t.checkbox.boxBg;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 14px',
        boxSizing: 'border-box',
        borderRadius: 12,
        border: `1px solid ${t.rule}`,
        background: t.cardBg,
        opacity: disabled ? 0.55 : 1
      }}>
      <span style={{ color: t.heading.color, display: 'flex' }} aria-hidden='true'>
        <SensorGlyph kind={biometry} size={20} />
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
        <span
          style={{
            ...typeStyle(t.label),
            fontSize: 12.5,
            letterSpacing: '.04em',
            textTransform: 'none'
          }}>
          Sign in with {name}
        </span>
        <span style={{ ...typeStyle(t.micro), textTransform: 'none', letterSpacing: 0 }}>
          {disabled
            ? unavailableReason
            : 'Uses this device’s screen lock. You can sign in another way anytime.'}
        </span>
        {/* The row STAYS in Settings when blocked (today it is hidden behind
            `biometricAvailableHere &&`, which is why the feature is invisible
            to anyone who hasn't set up a sensor). Dimmed and un-toggleable, but
            present — and when the player can do something about it, it says so
            in place rather than sending them hunting. */}
        {disabled && actionLabel && (
          <button
            onClick={onAction}
            style={{
              ...hitArea(),
              alignSelf: 'flex-start',
              marginTop: 2,
              background: 'none',
              border: 0,
              color: t.toggleColor,
              font: `600 11px/1.4 ${t.body.fontFamily}`,
              textDecoration: 'underline',
              cursor: 'pointer'
            }}>
            {actionLabel}
          </button>
        )}
      </div>
      <button
        role='switch'
        aria-checked={enabled}
        aria-label={`Sign in with ${name}`}
        disabled={disabled}
        onClick={() => onToggle?.(!enabled)}
        style={{
          ...hitArea(),
          background: 'transparent',
          border: 0,
          cursor: disabled ? 'default' : 'pointer',
          flex: 'none'
        }}>
        <span
          style={{
            display: 'block',
            width: 44,
            height: 26,
            borderRadius: 999,
            background: enabled ? trackOn : trackOff,
            border: t.checkbox.boxBorder,
            position: 'relative',
            transition: 'background 160ms ease-out'
          }}>
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: enabled ? 20 : 2,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,.4)',
              transition: 'left 180ms cubic-bezier(0.2, 0, 0, 1)'
            }}
          />
        </span>
      </button>
    </div>
  );
}

/* ── 8. Simulated OS prompt (playground-only) ────────────────────────────── */

/**
 * Stand-in for the OS biometric sheet. Deliberately system-styled (system-ui
 * font, monochrome) and clearly labelled: this surface belongs to iOS/Android,
 * not to us — the stories include it only so reviewers see WHERE the OS takes
 * over, and so the playground can branch on its outcomes. Not a deliverable.
 */
export function FakeOSPrompt({
  biometry,
  onMatch,
  onNoMatch,
  onCancel
}: {
  biometry: BiometryKind;
  onMatch?: () => void;
  onNoMatch?: () => void;
  onCancel?: () => void;
}) {
  return (
    <div
      style={{
        width: 250,
        borderRadius: 18,
        background: 'rgba(28,28,30,.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        color: '#fff',
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif",
        textAlign: 'center',
        padding: '20px 14px 10px',
        boxShadow: '0 18px 50px rgba(0,0,0,.55)'
      }}>
      <div style={{ display: 'flex', justifyContent: 'center', color: '#0a84ff' }}>
        <SensorGlyph kind={biometry} size={44} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, marginTop: 10 }}>
        {biometryName(biometry) === 'fingerprint' ? 'Fingerprint' : biometryName(biometry)}
      </div>
      <div style={{ fontSize: 12, opacity: 0.65, marginTop: 2, marginBottom: 14 }}>
        Sign in to Hijack Poker
      </div>
      <div
        style={{
          borderTop: '1px solid rgba(255,255,255,.15)',
          display: 'flex',
          flexDirection: 'column'
        }}>
        {[
          { label: 'Simulate match', fn: onMatch },
          { label: 'Simulate no match', fn: onNoMatch },
          { label: 'Cancel', fn: onCancel }
        ].map(({ label, fn }) => (
          <button
            key={label}
            onClick={fn}
            style={{
              background: 'transparent',
              border: 0,
              borderBottom: '1px solid rgba(255,255,255,.08)',
              color: '#0a84ff',
              fontSize: 14,
              padding: '11px 0',
              cursor: 'pointer',
              fontFamily: 'inherit'
            }}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export { CardVariantContext };
